import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../components/Button';
import styles from './RunModelingDialog.module.css';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface RunModelingDialogProps {
  /** The configured model, or null when the key omits one. */
  model: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Spend-confirmation dialog for a modeling run (#229). Patterned on the
 * capability ConsentDialog (role="dialog" + aria-modal, initial focus on Cancel,
 * full Tab/Shift+Tab trap, Esc = cancel with no POST, focus returns to the
 * trigger, background inert + scroll-locked). It shows NO dollar figure — there is
 * no modeling-run estimate (34-D3 adds cost); it honestly discloses that the run
 * spends against the customer's BYOK key and cost depends on journey size.
 */
export function RunModelingDialog({ model, busy = false, onConfirm, onCancel }: RunModelingDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const appRoot = document.getElementById('root');
    appRoot?.setAttribute('inert', '');
    cancelRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
    function onFocusIn(event: FocusEvent) {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        cancelRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn);
      appRoot?.removeAttribute('inert');
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onCancel]);

  return createPortal(
    <div className={styles.overlay}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="run-modeling-title"
        aria-describedby="run-modeling-desc"
        className={styles.dialog}
      >
        <h2 id="run-modeling-title" className={styles.title}>
          Run AI modeling?
        </h2>
        <p id="run-modeling-desc" className={styles.desc}>
          This uses {model ? <strong>{model}</strong> : 'your configured model'} and spends against
          your BYOK key. The cost depends on the journey&rsquo;s size and your provider&rsquo;s
          pricing &mdash; no modeling-run estimate is available yet. The result is an unverified
          draft model of this journey.
        </p>
        <div className={styles.actions}>
          <Button
            ref={cancelRef}
            type="button"
            variant="secondary"
            onClick={onCancel}
            data-testid="run-modeling-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            busy={busy}
            onClick={onConfirm}
            data-testid="run-modeling-confirm"
          >
            Run modeling
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
